/**
 * Export target descriptors — the one place a target's shape is declared.
 *
 * Before this module the target was threaded as two unrelated ad-hoc things:
 * a `mode: 'flat'|'11ty'` string (preflight, link-checker, confidence-report,
 * lock-manifest, client-info) and an `eleventyMode: boolean` (asset-resolver,
 * font-fetcher, preview-bundler). A third target could not be added without
 * finding every one of those call sites by hand, and a boolean cannot express
 * three states at all.
 *
 * Web-standard: pure data and pure functions, no `node:` imports, no builder
 * imports. `asset-resolver.js` runs in the browser and imports this.
 *
 * NOTE on `astro`: its descriptor is declared here in full, but only `flat` and
 * `eleventy` have consumers as of AS-0. The astro entry is exercised from AS-1
 * onward. Adding it here rather than later is deliberate — the descriptor is
 * what the AS-0 refactor exists to introduce, and a two-entry table would not
 * have proved that the shape generalizes.
 */

/**
 * Where one resolved asset lands in the emitted tree, and how HTML refers to it.
 *
 * @param {string} assetPath — target-relative path, e.g. `assets/images/hero.jpg`
 * @returns {{localPath: string, htmlPath: string, shortcodeSrc: string|null, importPath: string|null}}
 *   `localPath` is the ZIP entry. `htmlPath` is what a src/href uses.
 *   `shortcodeSrc` feeds 11ty's `{% image %}`. `importPath` feeds Astro's
 *   `getImage()` — non-null only when the target processes that asset.
 */
function flatPlacement(assetPath) {
  return { localPath: assetPath, htmlPath: assetPath, shortcodeSrc: null, importPath: null };
}

function eleventyPlacement(assetPath) {
  return {
    localPath: `src/${assetPath}`,
    htmlPath: `/${assetPath}`,
    shortcodeSrc: `./src/${assetPath}`,
    importPath: null
  };
}

/**
 * Astro processes `src/assets/**` (hashed, resized, reformatted by sharp) and
 * copies `public/**` verbatim, so an optimized image must live under `src/` and
 * be reached by ESM import rather than by URL.
 *
 * The resolver nevertheless places EVERYTHING under `public/`, and that is
 * deliberate. Whether a given image is optimized is not a property of its URL:
 * `isOptimizableImage()` (image-shortcode-rewriter.js:74) also excludes logos
 * and art-directed `<picture>` sources, and it needs the DOM element. The
 * resolver has only a URL and a blob, so any split it makes is a guess — and a
 * wrong guess puts the file somewhere the build never serves, which is a 404
 * rather than a missed optimization.
 *
 * AS-3's image usage manifest owns the split, because it owns the decision: for
 * each image it chooses to optimize it moves the asset to `src/assets/images/`
 * and rewrites the markup to an import. Everything it does not touch is already
 * in the right place and already addressable.
 *
 * (An earlier revision split here on file extension. AS-2 found the layering
 * error the first time a real export ran: the link check reported six
 * `broken-local-ref` findings, because the markup still referenced `/assets/…`
 * for files that had been placed under `src/`.)
 */
function astroPlacement(assetPath) {
  return {
    localPath: `public/${assetPath}`,
    htmlPath: `/${assetPath}`,
    shortcodeSrc: null,
    importPath: null
  };
}

/** @type {Readonly<Record<string, object>>} */
export const EXPORT_TARGETS = Object.freeze({
  flat: Object.freeze({
    id: 'flat',
    // Goes into `artifactState.exportMode` in the lock, and is the `mode`
    // string preflight / link-checker / confidence-report / client-info read.
    // `id` and `lockMode` differ for eleventy only, because the lock's value
    // was '11ty' before this module existed and is a persisted vocabulary.
    lockMode: 'flat',
    placeAsset: flatPlacement,
    fontPathPrefix: '',
    // Only targets that bundle the Roboto binaries may declare its @font-face.
    // Declaring faces in a target that does not ship the files points CSS at
    // missing assets, which preflight correctly rejects as dangling.
    bundlesRobotoFonts: false,
    staticSourceDir: '',
    buildOutputDir: '',
    processedImageDir: null,
    clientDataPath: null,
    // Two stamps: some hosts refuse to serve dot-directories.
    stampPaths: Object.freeze(['.well-known/pagestitch.json', 'pagestitch.json']),
    buildCommand: null,
    devCommand: null,
    deployCommand: null
  }),

  eleventy: Object.freeze({
    id: 'eleventy',
    lockMode: '11ty',
    placeAsset: eleventyPlacement,
    fontPathPrefix: 'src/',
    bundlesRobotoFonts: true,
    staticSourceDir: '',
    buildOutputDir: 'public',
    processedImageDir: null,
    clientDataPath: 'src/_data/client.json',
    stampPaths: Object.freeze(['src/.well-known/pagestitch.json']),
    buildCommand: 'eleventy',
    devCommand: 'eleventy --serve',
    deployCommand: 'wrangler pages deploy public/'
  }),

  astro: Object.freeze({
    id: 'astro',
    lockMode: 'astro',
    placeAsset: astroPlacement,
    // Fonts are never processed by Astro — they are referenced from CSS by a
    // root-absolute URL, so they belong in the verbatim-copy bucket.
    fontPathPrefix: 'public/',
    bundlesRobotoFonts: true,
    staticSourceDir: 'public',
    // NOT `public`. In 11ty `public/` is the build OUTPUT; in Astro it is the
    // static-asset SOURCE and the output is `dist/`. Getting this backwards
    // ships a repo that commits its own build output and deploys an empty site.
    buildOutputDir: 'dist',
    processedImageDir: 'src/assets/images',
    clientDataPath: 'src/data/client.json',
    stampPaths: Object.freeze(['public/.well-known/pagestitch.json']),
    buildCommand: 'astro build',
    devCommand: 'astro dev',
    deployCommand: 'wrangler pages deploy dist/'
  })
});

/**
 * @param {string} id — 'flat' | 'eleventy' | 'astro'
 * @returns {object} the frozen descriptor
 * @throws {Error} on an unknown id — a typo must fail loudly rather than
 *   silently falling back to flat, which would emit a tree for the wrong target.
 */
export function getExportTarget(id) {
  const target = EXPORT_TARGETS[id];
  if (!target) {
    throw new Error(
      `Unknown export target "${id}". Known targets: ${Object.keys(EXPORT_TARGETS).join(', ')}.`
    );
  }
  return target;
}

/**
 * The descriptor for a lock's recorded `exportMode`.
 *
 * The lock stores `lockMode` ('11ty'), not the descriptor id ('eleventy'), so
 * fleet tooling reading a lock has to come back the other way. Returns null for
 * an unknown mode — a lock written by a newer builder must not be silently
 * treated as one of the modes this build happens to know.
 */
export function targetForLockMode(mode) {
  return Object.values(EXPORT_TARGETS).find((target) => target.lockMode === mode) || null;
}

/** Every `exportMode` a lock may legally record. */
export const LOCK_MODES = Object.freeze(
  Object.values(EXPORT_TARGETS).map((target) => target.lockMode)
);

/** The `mode` string a target reports as, for preflight/link-checker/lock. */
export function lockModeFor(id) {
  return getExportTarget(id).lockMode;
}
